// ./src/styles/theme/index.ts
// ============================================
// The app theme, per brand variant:
//   • festive  — "Sunset Soirée": coral × grape × gold on warm plum-ink neutrals.
//   • business — iris (the logo's purple) × lagoon (its teal) on cool slate neutrals (ROADMAP #66).
// ============================================

import {
  createTheme,
  CSSVariablesResolver,
  defaultVariantColorsResolver,
  MantineThemeOverride,
  VariantColorsResolver,
} from "@mantine/core";
import { ThemeVariant } from "./variants";
import {
  coral,
  grape,
  gold,
  emerald,
  crimson,
  amber,
  ink,
  plumDark,
  iris,
  orchid,
  azure,
  lagoon,
  slate,
  lime,
  brandSurfaces,
} from "./colors";
import { fonts } from "./fonts";
import { getComponents } from "./components";

/**
 * The hues each Mantine colour NAME resolves to, per variant. A name is a MEANING used across ~100 files
 * (`color="grape"` = a category such as Receive / scheduled / private, `blue` = info-ish, `teal` = done), so the
 * variant re-points the name rather than every file: on business the primary is purple, and `grape` / `violet` /
 * `blue` move to hues that cannot be mistaken for it (≥ 40° away — `check-brand-theme`).
 */
const variantHues = (variant: ThemeVariant) =>
  variant === "business"
    ? { primary: iris, category: orchid, neutral: slate }
    : { primary: coral, category: grape, neutral: ink };

/**
 * The LIGHT HUES (yellow / gold / lime): their shade 6 is bright, so a FILLED surface takes dark text (autoContrast) and
 * looks like the colour it names — but the same shade as TEXT on its own wash (a light badge, an outline, a subtle
 * button) would be unreadable, so those variants take shade 8. Every other colour keeps Mantine's default.
 * ⚠ `check-brand-theme` reads this list to measure the text each variant really gets.
 */
export const LIGHT_HUES = ["yellow", "gold", "lime", "accent"] as const;

const lightHueTextVariants = new Set(["light", "outline", "subtle", "transparent"]);

const variantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input);
  const name = typeof input.color === "string" ? input.color.split(".")[0] : "";

  if (!(LIGHT_HUES as readonly string[]).includes(name) || !lightHueTextVariants.has(String(input.variant))) {
    return resolved;
  }

  return { ...resolved, color: `var(--mantine-color-${name}-8)` };
};

/**
 * Per-brand theme factory. The variants differ in colour (above) and personality:
 *   • festive  (PartyLabz)   — coral; expressive Fraunces serif headings, rounder.
 *   • business (eventsmagic) — iris purple + lagoon teal; clean grotesque headings, tighter radii.
 */
export const getBrandTheme = (
  variant: ThemeVariant = "festive"
): MantineThemeOverride => {
  const festive = variant === "festive";
  const headingFont = festive ? fonts.display : fonts.primary;
  const hues = variantHues(variant);
  const surfaces = brandSurfaces(variant);
  const ink9 = hues.neutral[9];
  const shade = (alpha: number) => `rgb(${surfaces.shadowInk} / ${alpha})`;
  const glow = (hex: string) => `0 8px 24px -6px ${hex}59`; // 59 ≈ 35% alpha

  return createTheme({
  // ============================================
  // Colors
  // Built-in Mantine names are overridden so existing
  // color="red|green|blue|…" usages render on-brand.
  // ============================================
  colors: {
    // Brand + custom names
    brand: hues.primary,
    coral,
    grape: hues.category,
    gold,
    jade: lagoon,
    lime,
    iris,
    lagoon,
    // Legacy semantic names still referenced across the app
    primary: hues.primary,
    accent: gold,
    road: hues.category,
    fuel: lagoon,
    neutral: hues.neutral,
    success: emerald,
    error: crimson,
    warning: amber,
    // Built-in overrides for cohesion
    red: crimson,
    pink: crimson,
    green: emerald,
    // ⓘ #66 (RSVP statuses, owner 2026-10-06: "colors are very close"): blue / indigo are a true AZURE and teal / cyan
    //   the logo's LAGOON on both brands — periwinkle sat 23° from PartyLabz's grape and jade 19° from its green.
    teal: lagoon,
    cyan: lagoon,
    yellow: gold,
    orange: amber,
    violet: hues.category,
    blue: azure,
    indigo: azure,
    gray: hues.neutral,
    dark: plumDark,
  },

  primaryColor: "brand",
  primaryShade: { light: 6, dark: 5 },

  // Pick a readable foreground on filled surfaces automatically.
  autoContrast: true,
  // …and readable TEXT for the light hues on their own wash (`LIGHT_HUES`).
  variantColorResolver,
  luminanceThreshold: 0.32,

  white: "#ffffff",
  black: ink9,

  // ============================================
  // Typography
  // ============================================
  fontFamily: fonts.primary,
  fontFamilyMonospace: fonts.monospace,

  headings: {
    // Festive brands get the expressive serif; business brands a clean sans.
    fontFamily: headingFont,
    fontWeight: "600",
    sizes: {
      h1: { fontSize: fonts.sizes.h1, lineHeight: "1.1", fontWeight: "600" },
      h2: { fontSize: fonts.sizes.h2, lineHeight: "1.15", fontWeight: "600" },
      h3: { fontSize: fonts.sizes.h3, lineHeight: "1.2", fontWeight: "600" },
      h4: { fontSize: fonts.sizes.h4, lineHeight: "1.3", fontWeight: "600" },
      h5: { fontSize: fonts.sizes.h5, lineHeight: "1.4", fontWeight: "600" },
      h6: { fontSize: fonts.sizes.h6, lineHeight: "1.45", fontWeight: "600" },
    },
  },

  // ============================================
  // Spacing
  // ============================================
  spacing: {
    xs: "0.5rem",
    sm: "0.75rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
  },

  // ============================================
  // Radius — friendly, a touch playful
  // ============================================
  radius: {
    xs: festive ? "0.375rem" : "0.25rem",
    sm: festive ? "0.5rem" : "0.375rem",
    md: festive ? "0.75rem" : "0.5rem", // festive 12px · business 8px
    lg: festive ? "1rem" : "0.75rem",
    xl: festive ? "1.375rem" : "1rem",
  },

  // ============================================
  // Shadows — soft, warm plum-tinted depth
  // ============================================
  shadows: {
    xs: `0 1px 2px 0 ${shade(0.05)}`,
    sm: `0 2px 6px -1px ${shade(0.08)}, 0 1px 2px -1px ${shade(0.06)}`,
    md: `0 8px 20px -6px ${shade(0.12)}, 0 2px 6px -2px ${shade(0.08)}`,
    lg: `0 16px 36px -10px ${shade(0.16)}, 0 6px 12px -6px ${shade(0.1)}`,
    xl: `0 28px 56px -16px ${shade(0.22)}, 0 10px 20px -10px ${shade(0.12)}`,
  },

  defaultRadius: "md",
  cursorType: "pointer",
  respectReducedMotion: true,
  focusRing: "auto",
  scale: 1,

  components: getComponents(),

  // ============================================
  // Custom Properties
  // ============================================
  other: {
    brandPrimary: hues.primary[6],
    brandSecondary: festive ? grape[6] : lagoon[5],
    accentGold: gold[5],
    accentJade: lagoon[5],

    successColor: emerald[5],
    errorColor: crimson[5],
    warningColor: amber[5],
    infoColor: lagoon[5],

    backgrounds: surfaces.backgrounds,
    text: surfaces.text,
    borders: surfaces.borders,
    gradients: surfaces.gradients,
    charts: surfaces.charts,
    semantic: surfaces.semantic,

    // Signature glows / brand shadows
    shadowBrand: glow(hues.primary[6]),
    shadowGrape: glow(hues.category[6]),
    glowGold: "0 0 24px rgb(240 180 41 / 0.45)",

    animations: {
      instant: "50ms",
      fast: "150ms",
      normal: "250ms",
      slow: "350ms",
      slower: "500ms",
    },

    easing: {
      default: "cubic-bezier(0.4, 0, 0.2, 1)",
      in: "cubic-bezier(0.4, 0, 1, 1)",
      out: "cubic-bezier(0, 0, 0.2, 1)",
      inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      bounce: "cubic-bezier(0.34, 1.56, 0.64, 1)",
    },

    zIndex: {
      hide: -1,
      base: 0,
      dropdown: 1000,
      sticky: 1020,
      fixed: 1030,
      modalBackdrop: 1040,
      modal: 1050,
      popover: 1060,
      tooltip: 1070,
      notification: 1080,
    },

    container: {
      xs: "540px",
      sm: "720px",
      md: "960px",
      lg: "1140px",
      xl: "1320px",
      full: "100%",
    },

    layout: {
      sidebarCollapsed: "72px",
      sidebarExpanded: "280px",
      headerHeight: "64px",
      mobileHeaderHeight: "56px",
      footerHeight: "48px",
    },
  },
  });
};

/**
 * The app's global CSS tokens that are not Mantine's own: the canvas (`--em-app-canvas`, read by `--ce-bg-primary` in
 * apps/app `variables.css`) and the inverse text. Pass to `<MantineProvider cssVariablesResolver>` beside the theme so
 * the page background follows the variant. Brand and neutral tokens need nothing here — `variables.css` maps them
 * onto `--mantine-primary-color-*` / `--mantine-color-gray-*`.
 */
export const brandCssVariablesResolver: CSSVariablesResolver = (theme) => ({
  variables: {
    "--em-app-canvas": String(theme.other?.backgrounds?.app ?? "#ffffff"),
    "--em-text-inverse": String(theme.other?.text?.inverse ?? "#ffffff"),
  },
  light: {},
  dark: {},
});

/** Default theme (festive). Prefer getBrandTheme(brand.themeVariant). */
export const eventsMagicTheme = getBrandTheme("festive");

// ============================================
// Re-exports for convenience
// ============================================
export { fonts, textStyles, googleFontsUrl, fontFaceCSS } from "./fonts";
export {
  coral,
  lime,
  iris,
  orchid,
  azure,
  lagoon,
  slate,
  brandSurfaces,
  grape,
  gold,
  jade,
  emerald,
  crimson,
  amber,
  periwinkle,
  ink,
  brand,
  accent,
  road,
  fuel,
  success,
  error,
  warning,
  neutral,
  semanticColors,
  gradients,
  backgrounds,
  textColors,
  borderColors,
  expenseColors,
  chartColors,
  colorPalette,
} from "./colors";

export default eventsMagicTheme;
